// © Microsoft Corporation. All rights reserved.
import { AppType, PageType, WebWorkerMessageId, environment, logger, perfMarker, registerCommandMessageHandler, sendMessage, webWorkerCache } from "../wwCommon";
import { TargetType } from "@msnews/pdp-service-contracts";
import { aadTokenQsKey, aadTokenQsVal, getOneServiceHeaders, shouldMakeEnterpriseRequest, shouldUseEnterpriseDomain } from "./OneServiceRequest";
import { FetchPdpDataFailed } from "../AppErrors";
import { tryReformatGuid } from "./Common";
/** Registers PDP related tasks */
export function registerPdpTasks() {
    if (!isPdpDataFeatureEnabled()) {
        return;
    }
    const subscription = webWorkerCache.subscribeMultiple([WebWorkerMessageId.AppAnonCookie, WebWorkerMessageId.MyFeed], (values) => {
        subscription.unsubscribe();
        const appAnonCookieExists = values[0] && values[0].payload && values[0].payload.appAnonCookieExists;
        if (appAnonCookieExists === true) {
            fetchUserPdpData();
        }
    });
    registerCommandMessageHandler(WebWorkerMessageId.Updatepdp, fetchUserPdpData);
    registerCommandMessageHandler(WebWorkerMessageId.InvalidateWeatherPdp, invalidateWeatherPdp);
}
/** Makes weather, money and sports PDP calls */
function fetchUserPdpData() {
    perfMarker("SdPDPBulkFetchStart");
    const pdpInfo = [
        { id: WebWorkerMessageId.WeatherPdp, type: TargetType.Location },
        { id: WebWorkerMessageId.SportsTeamPdp, type: TargetType.Team },
        { id: WebWorkerMessageId.SportsPlayerPdp, type: TargetType.Player },
        { id: WebWorkerMessageId.SportsLeaguePdp, type: TargetType.League },
        { id: WebWorkerMessageId.MoneyPdp, type: TargetType.Finance }
    ];
    const url = getPdpUrl(pdpInfo.map(info => info.type));
    const init = {
        credentials: "include",
        method: "GET",
        headers: getOneServiceHeaders()
    };
    return fetch(url, init).then(response => {
        if (!response.ok && response.status !== 401) {
            throw (`SD card bulk PDP call failed with ${response.status} and StatusText: ${response.statusText}`);
        }
        return response.json();
    }).then((data) => {
        perfMarker("SdPDPBulkFetchEnd");
        const valueGroups = {};
        (data.value || []).forEach(value => {
            const values = valueGroups[value.targetType] || (valueGroups[value.targetType] = []);
            values.push(value);
        });
        pdpInfo.forEach(info => {
            // de-multiplex the batch result into individual results base on the inputs
            // and handle them individually
            const infoSlice = valueGroups[info.type] || [];
            fetchPdpComplete({ value: infoSlice }, info.id);
        });
    }).catch(reason => {
        perfMarker("SdPDPBulkFetchError");
        logger.error(Object.assign(Object.assign({}, FetchPdpDataFailed), { message: "PDP bulk data fetch failed for SD cards", pb: Object.assign(Object.assign({}, FetchPdpDataFailed.pb), { customMessage: `Error: ${reason}. Url: ${url}` }) }));
        // de-multiplex the batch error
        pdpInfo.forEach(info => {
            fetchPdpComplete(null, info.id);
        });
    });
}
/**
 * Marks cache state complete for PDP Data fetch. Responds to any pending messages.
 * @param response - The response
 * @param id - The webworker messageId
 */
function fetchPdpComplete(response, id) {
    logger.info(`WebWorker.fetchPDPComplete: [id ${id}]`);
    webWorkerCache.set(id, { payload: response });
    sendMessage({ id, fetched: true, payload: response });
}
/**
 * Invalidates pdp calls
 */
export function invalidateWeatherPdp() {
    logger.info(`WebWorker.InvalidatePdp: [id ${WebWorkerMessageId.InvalidateWeatherPdp}]`);
    webWorkerCache.delete(WebWorkerMessageId.WeatherPdp);
    sendMessage({ id: WebWorkerMessageId.InvalidateWeatherPdp, fetched: false, payload: null });
}
/**
 * Returns the pdp get Url
 * @param targetType - The target type to filter
 * @returns - The url
 */
function getPdpUrl(targetTypes) {
    const isEnterpriseRequest = shouldMakeEnterpriseRequest();
    const userId = environment.userId && environment.userId.length ? `&user=${environment.userId}` : "";
    const activityId = environment.activityId && environment.activityId.length ? `&activityId=${tryReformatGuid(environment.activityId)}` : "";
    const fdhead = environment.currentFlights && environment.currentFlights.length ? `&fdhead=${environment.currentFlights}` : "";
    const ocid = "&ocid=Peregrine";
    const appAnonScn = "&scn=APP_ANON";
    const anonScn = "&scn=ANON";
    const aad = isEnterpriseRequest ? `&${aadTokenQsKey}=${aadTokenQsVal}` : "";
    const urlBase = shouldUseEnterpriseDomain() ? environment.enterpriseServiceUrlBase : environment.feedServiceUrlBase;
    const targetTypeFilter = targetTypes.map(targetType => `targetType eq '${targetType}'`).join(" or ");
    const filterQueryString = `$filter=actionType eq 'Follow' and (${targetTypeFilter})`;
    // default count is 10 for individual calls, since we can't really guarantee we'll get 10 for each
    // during the bulk call, we'll use the count as a multipler factor and double it as a loose safety measure.
    const countFilter = `$top=${targetTypes.length * 10 * 2}`;
    let scn = "";
    switch (environment.pagetype) {
        case PageType.MMX:
        case PageType.DHP:
        case PageType.NTP:
            scn = appAnonScn;
            break;
        default:
            scn = anonScn;
    }
    const url = new URL(`graph/actions${urlBase.search || "?"}&apiKey=${environment.oneServiceApiKey}${activityId}${ocid}&market=${environment.locale}${userId}${fdhead}${scn}&${filterQueryString}&${countFilter}${aad}`, urlBase);
    return url.href;
}
/**
 * Checks if PDP data feature is enabled
 */
export function isPdpDataFeatureEnabled() {
    if (environment.isEnterpriseCompliantNTP) {
        return false;
    }
    return environment.apptype === AppType.Edge || environment.apptype === AppType.EdgeChromium || environment.apptype === AppType.Windows;
}
/**
 * Test only exports
 */
export const _fetchUserPdpData = fetchUserPdpData;
//# sourceMappingURL=PdpCardData.js.map